import { createStore } from "solid-js/store";
function normalizeMethodsPayload(input) {
    if (!Array.isArray(input)) {
        return [];
    }
    const seen = new Set();
    const methods = [];
    for (const rawMethod of input) {
        const method = String(rawMethod ?? "").trim();
        if (!method || seen.has(method)) {
            continue;
        }
        seen.add(method);
        methods.push(method);
    }
    return methods;
}
/**
 * Multi-Factor Authentication
 */
export class MFA {
    #client;
    #store;
    #resolvedMethods;
    /**
     * Construct MFA helper
     * @param client Client
     * @param state State
     */
    constructor(client, state) {
        this.#client = client;
        this.#store = createStore(state);
        this.#resolvedMethods = null;
    }
    /**
     * Whether authenticator app is enabled
     */
    get authenticatorEnabled() {
        return this.#store[0].totp_mfa;
    }
    /**
     * Whether recovery codes are enabled
     */
    get recoveryEnabled() {
        return this.#store[0].recovery_active;
    }
    /**
     * Available methods for generating tickets.
     *
     * An account with no second factor proves itself with its password; there
     * is never an empty list and never a ticket without a proof.
     */
    get availableMethods() {
        if (this.#resolvedMethods && this.#resolvedMethods.length > 0) {
            return this.#resolvedMethods;
        }
        if (!this.authenticatorEnabled) {
            return ["Password"];
        }
        const methods = ["Totp"];
        if (this.recoveryEnabled) {
            methods.push("Recovery");
        }
        return methods;
    }
    /**
     * Fetch the available methods from the API.
     * @throws if the request fails or does not answer with a list of methods
     */
    async fetchAvailableMethods() {
        const response = (await this.#client.api.get("/auth/mfa/methods"));
        // The API answers with a bare list; `{ methods }` is accepted as well.
        const methods = normalizeMethodsPayload(response && typeof response === "object" && !Array.isArray(response)
            ? response.methods
            : response);
        if (methods.length === 0) {
            throw new Error("Could not determine how to confirm this action.");
        }
        this.#resolvedMethods = methods;
        return methods;
    }
    /**
     * Create an MFA ticket
     * @param params
     * @returns Token
     */
    async createTicket(params) {
        return new MFATicket(this.#client, await this.#client.api.put("/auth/mfa/ticket", params), this.#store[1]);
    }
    /**
     * Enable the authenticator with a code from the secret issued earlier in
     * this session. No ticket: the pending secret is bound to the session that
     * requested it, and a correct code from it is the proof.
     * @param code Code from the authenticator app
     */
    async enableAuthenticator(code) {
        await this.#client.api.put("/auth/mfa/totp", { totp_code: code });
        this.#resolvedMethods = null;
        this.#store[1]("totp_mfa", true);
        this.#store[1]("recovery_active", false);
    }
}
/**
 * MFA Ticket
 */
export class MFATicket {
    #client;
    #ticket;
    #mutate;
    #used = false;
    /**
     * Construct MFA Ticket
     * @param client Client
     * @param ticket Ticket
     * @param mutate Mutate the store
     */
    constructor(client, ticket, mutate) {
        this.#client = client;
        this.#ticket = ticket;
        this.#mutate = mutate;
    }
    /**
     * Token
     */
    get token() {
        return this.#ticket.token;
    }
    /**
     * Request options carrying this ticket
     */
    #ticketHeaders() {
        return {
            headers: {
                "X-MFA-Ticket": this.token,
            },
        };
    }
    /**
     * Use the ticket
     */
    #consume() {
        if (this.#used)
            throw "Already used this ticket!";
        this.#used = true;
    }
    /**
     * Generate new set of recovery codes
     * @returns List of codes
     */
    async generateRecoveryCodes() {
        this.#consume();
        const codes = await this.#client.api.patch("/auth/mfa/recovery", undefined, this.#ticketHeaders());
        this.#mutate("recovery_active", true);
        return codes;
    }
    /**
     * Generate new authenticator secret
     * @returns Secret
     */
    async generateAuthenticatorSecret() {
        this.#consume();
        return (await this.#client.api.post("/auth/mfa/totp", undefined, this.#ticketHeaders())).secret;
    }
    /**
     * Disable authenticator. This also deletes the account's recovery codes.
     */
    async disableAuthenticator() {
        this.#consume();
        await this.#client.api.delete("/auth/mfa/totp", undefined, this.#ticketHeaders());
        this.#mutate("totp_mfa", false);
        this.#mutate("recovery_active", false);
    }
    /**
     * Disable account
     */
    disableAccount() {
        this.#consume();
        return this.#client.api.post("/auth/account/disable", undefined, this.#ticketHeaders());
    }
    /**
     * Request deletion of the account
     * @returns How the request is confirmed: `"email"` if a confirmation link
     * was sent and nothing changes until it is opened, `"none"` if the account
     * was scheduled for deletion at once (its sessions are then revoked)
     */
    async deleteAccount() {
        this.#consume();
        const response = (await this.#client.api.post("/auth/account/delete", undefined, this.#ticketHeaders()));
        return { confirmation: response?.confirmation === "none" ? "none" : "email" };
    }
}
